import Downshift from 'downshift';
import { matchSorter } from 'match-sorter';
import styled, { css } from 'styled-components';
import { countryDialCodes, getCountry } from '../../../helpers/countryDialCodes';
import {
  ItemBackground,
  alignItemsFlexStart,
  borderABlue,
  flexBoxCenter,
  justifyContentSpaceBetween,
  layerA180Deg,
  layerBDark,
  scrollbarY,
} from '../../styles/commonStyles';

// Dial-code picker shown in front of the work phone and cellphone inputs.
// `value` is an ISO country code such as 'AE', or null when nothing is picked.
const CountryCodeSelect = ({ value, onChange, isMobile }) => {
  const itemToString = (item) => (item ? item.dial : '');

  // Matching on the name, dial code and ISO code lets the user find a country
  // by typing either '971' or 'emirates'.
  const getItems = (inputValue) =>
    inputValue
      ? matchSorter(countryDialCodes, inputValue, {
          keys: ['name', 'dial', 'code'],
        })
      : countryDialCodes;

  return (
    <Downshift
      selectedItem={getCountry(value)}
      itemToString={itemToString}
      onChange={(item) => item && onChange(item.code)}
    >
      {({
        getInputProps,
        getMenuProps,
        getItemProps,
        getRootProps,
        highlightedIndex,
        inputValue,
        isOpen,
        openMenu,
      }) => {
        const items = getItems(inputValue);

        return (
          <Root {...getRootProps()} isMobile={isMobile}>
            <Trigger isMobile={isMobile}>
              <TriggerInput
                {...getInputProps({
                  placeholder: 'code',
                  onFocus: openMenu,
                  onClick: openMenu,
                })}
              />
              <Caret>{isOpen ? '▴' : '▾'}</Caret>
            </Trigger>

            <List {...getMenuProps()} isOpen={isOpen} isMobile={isMobile}>
              {isOpen && (
                <ListInner>
                  {items.length === 0 ? (
                    <ListItemWrapper as='div'>
                      <ListItem as='p'>no matching country</ListItem>
                    </ListItemWrapper>
                  ) : (
                    items.map((item, index) => (
                      <ListItemWrapper
                        key={item.code}
                        isHighlighted={highlightedIndex === index}
                        {...getItemProps({ item, index })}
                      >
                        <ListItem>
                          {item.dial} {item.name}
                        </ListItem>
                      </ListItemWrapper>
                    ))
                  )}
                </ListInner>
              )}
            </List>
          </Root>
        );
      }}
    </Downshift>
  );
};

export default CountryCodeSelect;

const Root = styled.div`
  ${({ isMobile }) =>
    isMobile
      ? css`
          width: 62px;
          height: 32px;
        `
      : css`
          width: 86px;
          height: 28px;
        `}

  position: relative;
`;

const Trigger = styled.div`
  width: 100%;
  height: 100%;

  background: transparent;
  ${borderABlue}
  border-radius: 16px;

  ${justifyContentSpaceBetween}
`;

const TriggerInput = styled.input`
  width: 100%;
  min-width: 0;
  margin-left: 8px;

  background-color: inherit;
  font-size: 10px;
  letter-spacing: 1px;
  color: #ffffff;

  ::placeholder {
    color: #808080;
  }
`;

const Caret = styled.span`
  margin-right: 8px;
  font-size: 8px;
  color: #808080;
`;

const List = styled.ul`
  ${({ isOpen }) =>
    !isOpen &&
    css`
      display: none;
    `}

  ${({ isMobile }) =>
    isMobile
      ? css`
          width: 190px;
          top: 34px;
        `
      : css`
          width: 210px;
          top: 30px;
        `}

  max-height: 168px;
  padding: 2px;
  left: 0;

  position: absolute;
  z-index: 100;

  ${alignItemsFlexStart}
  ${layerA180Deg}
  border-radius: 12px;

  ${scrollbarY}
  ::-webkit-scrollbar {
    display: none;
  }
`;

const ListInner = styled.div`
  width: 100%;
  padding: 2px;

  ${layerBDark}
  border-radius: 10px;

  ${flexBoxCenter}
  flex-direction: column;
`;

const ListItemWrapper = styled.li`
  width: 98%;
  height: 16px;

  ${ItemBackground}

  background-color: ${({ isHighlighted }) =>
    isHighlighted && `hsla(50deg, 100%, 80%, 0.25)`};
  &:hover {
    background-color: hsla(50deg, 100%, 80%, 0.25);
  }

  margin-bottom: 2px;
  :last-child {
    margin-bottom: 0px;
  }

  cursor: pointer;
  overflow: hidden;
  ${flexBoxCenter}
`;

const ListItem = styled.span`
  width: 100%;
  margin-left: 8px;

  font-size: 8px;
  letter-spacing: 0.8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
